import { useId, useState } from 'react';
import type { AvisoMudancaEstado, MotivoDto, ProcessoCartaoDto } from '@nexital/shared';
import { Dialogo } from '../design-system/Dialogo';

/** O que a tela pergunta antes de mudar o estado de um cartão. */
export type PerguntaEstado =
  | { tipo: 'sem-estado'; processo: ProcessoCartaoDto }
  | { tipo: 'lixeira'; processo: ProcessoCartaoDto }
  | { tipo: 'arquivo'; processo: ProcessoCartaoDto; motivos: MotivoDto[] }
  | { tipo: 'avisos'; avisos: AvisoMudancaEstado[] };

type Props = { pergunta: PerguntaEstado; responder: (valor: string | null) => void };

const nomeDo = (p: ProcessoCartaoDto) => `${p.numero} — ${p.objeto}`;

/** Caixas de confirmação da mudança de estado. Responde o texto (motivo) ou o uuid do motivo; null = cancelou. */
export function DialogoEstado({ pergunta, responder }: Props) {
  const [texto, definirTexto] = useState('');
  const id = useId();
  const cancelar = () => responder(null);

  if (pergunta.tipo === 'sem-estado') {
    return (
      <Dialogo
        titulo="O cartão vai para Aguardando análise"
        descricao={`${nomeDo(pergunta.processo)} foi solto fora de qualquer grupo e ficará na bandeja Aguardando análise.`}
        rotuloConfirmar="Mover para Aguardando análise"
        aoConfirmar={() => responder('')}
        aoCancelar={cancelar}
      />
    );
  }
  if (pergunta.tipo === 'arquivo') return <DialogoArquivo processo={pergunta.processo} motivos={pergunta.motivos} responder={responder} />;

  const lixeira = pergunta.tipo === 'lixeira';
  const campo = (
    <label className="campo" htmlFor={`${id}-motivo`}>
      <span>{lixeira ? 'Motivo (opcional)' : 'Motivo (obrigatório)'}</span>
      <textarea id={`${id}-motivo`} rows={3} maxLength={500} value={texto} onChange={(e) => definirTexto(e.target.value)} autoFocus />
    </label>
  );
  if (lixeira) {
    return (
      <Dialogo
        titulo="Enviar à Lixeira?"
        descricao={`${nomeDo(pergunta.processo)} sai do quadro. Você pode restaurá-lo depois pela Lixeira.`}
        rotuloConfirmar="Enviar à Lixeira"
        perigo
        aoConfirmar={() => responder(texto.trim())}
        aoCancelar={cancelar}
      >
        {campo}
      </Dialogo>
    );
  }
  return (
    <Dialogo
      titulo="Confirme a mudança de estado"
      descricao={
        <ul className="dialogo__avisos">
          {pergunta.avisos.map((a) => (
            <li key={a.codigo}>{a.mensagem}</li>
          ))}
        </ul>
      }
      rotuloConfirmar="Mudar mesmo assim"
      podeConfirmar={texto.trim().length > 0}
      aoConfirmar={() => responder(texto.trim())}
      aoCancelar={cancelar}
    >
      {campo}
    </Dialogo>
  );
}

function DialogoArquivo({ processo, motivos, responder }: { processo: ProcessoCartaoDto; motivos: MotivoDto[]; responder: Props['responder'] }) {
  const [motivo, definirMotivo] = useState<string | null>(null);
  const nome = useId();
  return (
    <Dialogo
      titulo="Arquivar processo"
      descricao={`${nomeDo(processo)} sai do quadro e passa a ser somente leitura.`}
      rotuloConfirmar="Arquivar"
      podeConfirmar={motivo !== null}
      aoConfirmar={() => responder(motivo)}
      aoCancelar={() => responder(null)}
    >
      <fieldset className="campo-opcoes">
        <legend>Motivo do arquivamento</legend>
        {motivos.map((m) => (
          <label key={m.uuid} className="campo-opcoes__opcao">
            <input type="radio" name={nome} value={m.uuid} checked={motivo === m.uuid} onChange={() => definirMotivo(m.uuid)} required />
            {m.descricao}
          </label>
        ))}
      </fieldset>
    </Dialogo>
  );
}
